import { useMemo } from 'react'

import { HelpOutline } from '@mui/icons-material'
import { Box, Tooltip, Typography } from '@mui/material'
import { format } from 'date-fns'

import { IGridColumn } from 'components/common'
import { DATE_TIME_SECONDS_FORMAT } from 'constants/DateFormats'
import { currencyFormatter } from 'helpers'

const useGetExportBookingsColumns = () => {
  const columns: IGridColumn[] = useMemo(
    () => [
      {
        field: 'exportable',
        renderHeader: () => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip
              title={
                <Typography variant='body2'>
                  This field indicates whether a record can be exported. A record is considered
                  valid only if <strong>Customer</strong> has an HubSpot record Id and{' '}
                  <strong>Contact</strong> has an HubSpot record Id. Otherwise, the record is
                  considered invalid and cannot be exported.
                </Typography>
              }
              arrow>
              <HelpOutline sx={{ fontSize: 16, cursor: 'help', mr: 0.5 }} />
            </Tooltip>
            <strong>Exportable</strong>
          </Box>
        ),
        minWidth: 150,
        valueGetter: (params: any) => (params.row.exportable ? 'Yes' : 'No'),
        cellClassName: (params: any) => {
          return params.row.exportable ? 'cell-exportable' : 'cell-not-exportable'
        },
      },
      {
        field: 'exportStatus',
        headerName: 'Export Status',
        minWidth: 150,
        cellClassName: (params: any) => {
          const status = params.row.exportStatus
          if (status === 'Not Synced') return 'cell-not-synced'
          if (status === 'Synced') return 'cell-synced'
          if (status === 'Failed') return 'cell-failed'
          return ''
        },
      },
      {
        field: 'ourReference',
        headerName: 'FPL Number',
        minWidth: 150,
        sortPath: ['id'],
        valueGetter: (params: any) => params.row.booking?.ourReference || '--',
      },
      {
        field: 'hubSpotSyncDate',
        headerName: 'Timestamp of export attempt',
        minWidth: 250,
        sortPath: ['booking', 'hubSpotSyncDate'],
        valueGetter: (params: any) => params.row.booking?.hubSpotSyncDate,
        valueFormatter: (params: any) =>
          params.value ? new Date(params.value).toLocaleString('en-GB') : '--',
      },
      {
        field: 'hubSpotObjectId',
        headerName: 'HS Record ID',
        minWidth: 150,
        sortPath: ['booking', 'hubSpotObjectId'],
        valueGetter: (params: any) => params.row.booking?.hubSpotObjectId || '--',
      },
      {
        field: 'customerName',
        renderHeader: () => (
          <Box sx={{ whiteSpace: 'normal', lineHeight: 'normal' }}>
            <strong>Customer Name</strong>
            <br />
            (Deal Name)
          </Box>
        ),
        minWidth: 250,
        valueGetter: (params: any) => params.row.customerName || '--',
      },
      {
        field: 'revenue',
        renderHeader: () => (
          <Box sx={{ whiteSpace: 'normal', lineHeight: 'normal' }}>
            <strong>Revenue</strong>
            <br />
            (Amount)
          </Box>
        ),
        minWidth: 150,
        valueFormatter: (params: any) =>
          params.value ? currencyFormatter.format(params.value) : '--',
      },
      {
        field: 'firstCollectionBy',
        renderHeader: () => (
          <Box sx={{ whiteSpace: 'normal', lineHeight: 'normal' }}>
            <strong>First Collection By</strong>
            <br />
            (Close Date)
          </Box>
        ),
        sortable: false,
        minWidth: 200,
        valueGetter: (params: any) => params.row.booking?.edgeAddresses?.firstCollectionBy,
        valueFormatter: (params: any) =>
          params.value ? format(new Date(params.value), DATE_TIME_SECONDS_FORMAT) : '--',
      },
      {
        field: 'lastDeliveryCity',
        renderHeader: () => (
          <Box sx={{ whiteSpace: 'normal', lineHeight: 'normal' }}>
            <strong>Last Delivery City</strong>
            <br />
            (Delivering To)
          </Box>
        ),
        sortable: false,
        minWidth: 200,
        valueGetter: (params: any) => params.row.booking?.edgeAddresses?.lastDeliveryCity || '--',
      },
      {
        field: 'firstCollectionCity',
        renderHeader: () => (
          <Box sx={{ whiteSpace: 'normal', lineHeight: 'normal' }}>
            <strong>First Collection City</strong>
            <br />
            (Picked Up From)
          </Box>
        ),
        sortable: false,
        minWidth: 250,
        valueGetter: (params: any) =>
          params.row.booking?.edgeAddresses?.firstCollectionCity || '--',
      },
      {
        field: 'serviceCode',
        renderHeader: () => (
          <Box sx={{ whiteSpace: 'normal', lineHeight: 'normal' }}>
            <strong>Service Code</strong>
            <br />
            (Service Type)
          </Box>
        ),
        minWidth: 200,
        valueGetter: (params: any) => params.row.serviceCode || '--',
      },
      {
        field: 'tariffCategoryName',
        renderHeader: () => (
          <Box sx={{ whiteSpace: 'normal', lineHeight: 'normal' }}>
            <strong>Tariff Category Name</strong>
            <br />
            (Vehicle)
          </Box>
        ),
        minWidth: 200,
        valueGetter: (params: any) => params.row.tariffCategoryName || '--',
      },
      {
        field: 'statusLabel',
        sortPath: ['booking', 'status'],
        renderHeader: () => (
          <Box sx={{ whiteSpace: 'normal', lineHeight: 'normal' }}>
            <strong>Status</strong>
            <br />
            (Deal Stage)
          </Box>
        ),
        minWidth: 250,
        valueGetter: (params: any) => params.row.booking?.statusLabel || '--',
      },
      {
        field: 'portal',
        headerName: 'Portal',
        minWidth: 100,
        sortPath: ['booking', 'isCreatedByCustomer'],
        valueGetter: (params: any) => (params.row.booking?.isCreatedByCustomer ? 'Yes' : '--'),
      },
      {
        field: 'currency',
        headerName: 'Currency',
        minWidth: 100,
        sortable: false,
        valueGetter: (params: any) => params.row.currency || '--',
      },
      {
        field: 'takenBy',
        renderHeader: () => (
          <Box sx={{ whiteSpace: 'normal', lineHeight: 'normal' }}>
            <strong>Taken/Created By</strong>
            <br />
            (Deal Owner)
          </Box>
        ),
        minWidth: 250,
        sortPath: ['booking', 'takenBy'],
        valueGetter: (params: any) =>
          params.row.booking?.takenBy || params.row.booking?.createdBy || '--',
      },
      {
        field: 'contactName',
        renderHeader: () => (
          <Box sx={{ whiteSpace: 'normal', lineHeight: 'normal' }}>
            <strong>Contact Name</strong>
            <br />
            (Contact)
          </Box>
        ),
        minWidth: 200,
        valueGetter: (params: any) => params.row.contactName || '--',
      },
      {
        field: 'contactEmail',
        renderHeader: () => (
          <Box sx={{ whiteSpace: 'normal', lineHeight: 'normal' }}>
            <strong>Contact Email</strong>
            <br />
            (Contact)
          </Box>
        ),
        minWidth: 250,
        valueGetter: (params: any) => params.row.contactEmail || '--',
      },
    ],
    [],
  )

  return columns
}

export default useGetExportBookingsColumns
